import { DispatchCard } from 'src/entities/dispatch';
import { buildNoPlanShiftTruckIds } from 'src/components/dispatch/noPlanVehicle';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

/**
 * 自動配車の対象になる delivery（勤務）id を、選択行から集める。
 *
 * 対象は選んだ行の勤務だけ。車両単位に広げると、1 台に複数のドライバーが同じ時間帯で
 * 乗る勤務で、選んでいないドライバーの勤務にまで案件が割り付く。
 *
 * 「回転を追加」で作った行はサーバに実体が無く deliveryId が負になる。その行だけを
 * 選んだときに対象ゼロで弾くと、追加した回転へ配車できない。V3Plans は回転（cycle）
 * 単位で選んでも同じ車両の delivery を指す（V3Plans/Presenter.tsx:350）ので、
 * ここでも同じ車両の実在 delivery へ解決する。どの勤務のものかは決まらないので、
 * 同時に選ばれている勤務があればそれを優先する。
 *
 * Why not collectSelectedShiftTruckIds を使い回す: あちらは確定バッジ・確定 API 用で、
 * placeholder 行を意図的に除く。placeholder を確定対象にすると実体の無い勤務を
 * 確定しようとしてしまう。用途が違うので判定を共有しない。
 */
export function collectSelectedDeliveryIdsForPlanning(
  selectedRowIds: ReadonlySet<string>,
  rowGroups: readonly VehicleRowGroup[],
): number[] {
  const ids = new Set<number>();

  rowGroups.forEach((group) => {
    const selectedRows = group.rows.filter((r) => selectedRowIds.has(r.rowId));
    if (selectedRows.length === 0) return;

    const realIds = selectedRows
      .filter((r) => !r.source.isPlaceholderRow && r.source.deliveryId > 0)
      .map((r) => r.source.deliveryId);
    realIds.forEach((id) => ids.add(id));

    // placeholder しか選ばれていない車両だけ、同じ車両の実在行へ載せ替える
    if (realIds.length > 0) return;
    const fallback = group.rows.find((r) => !r.source.isPlaceholderRow && r.source.deliveryId > 0);
    if (fallback) ids.add(fallback.source.deliveryId);
  });

  return [...ids];
}

/**
 * 選んだ勤務がすべて「計画無」か。true なら未割当案件を選ばないと自動配車を押せない。
 *
 * 車両単位で見ると、1 台に複数のドライバーが同じ時間帯で乗る勤務では、別の人に配車が
 * あるだけで「組み直せる」と判定され、割り当てるものが無いまま実行できてしまう。
 *
 * 回転追加の行は実体が無いので、載せ先（collectSelectedDeliveryIdsForPlanning と
 * 同じ規則で決まる勤務）の状態で判定する。
 */
export function allSelectedShiftsHaveNoPlan(
  selectedRowIds: ReadonlySet<string>,
  rowGroups: readonly VehicleRowGroup[],
  /** 「配車がある」の見方。カード配車は未反映の D&D も数える（noPlanVehicle 参照） */
  hasPlanCards?: (cards: readonly DispatchCard[]) => boolean,
): boolean {
  return rowGroups.every((group) => {
    const selectedRows = group.rows.filter((r) => selectedRowIds.has(r.rowId));
    if (selectedRows.length === 0) return true;

    // 同じ勤務の中は回転をまたいで見るため、車両の全行から作る
    const noPlanShiftTruckIds = buildNoPlanShiftTruckIds(group.rows, hasPlanCards);
    const fallbackShiftTruckId = selectedRows.find((r) => r.shiftTruckId != null)?.shiftTruckId
      ?? group.rows.find((r) => r.shiftTruckId != null)?.shiftTruckId
      ?? null;

    return selectedRows.every((r) => {
      const shiftTruckId = r.shiftTruckId ?? fallbackShiftTruckId;
      // 載せ先の勤務が無い車両は、配車できる実体が無いので「計画無」と同じ扱い
      if (shiftTruckId == null) return true;
      return noPlanShiftTruckIds.has(shiftTruckId);
    });
  });
}
